import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, Box, Typography, CircularProgress, ToggleButton, ToggleButtonGroup,
    List, ListItem, ListItemText, ListItemButton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { formatDateDisplay } from '../../utils/dateUtils';
import * as API from '../../api';

// --- CÁC HÀM HELPER ĐỂ ĐỒNG BỘ NỘI DUNG VỚI CÁC POPUP KHÁC ---
const BOOKING_CATEGORY_LABELS = {
    'TOUR': 'Tour trọn gói / Ghép', 'TRANSPORT': 'Dịch vụ Thuê xe', 'HOTEL': 'Dịch vụ Phòng KS', 
    'FLIGHT': 'Vé máy bay', 'COMBO': 'Combo Dịch vụ', 'OTHER': 'Dịch vụ Khác'
};
const DIRECTION_LABELS = { 'ONE_WAY_TO': 'Chiều đi', 'ONE_WAY_FROM': 'Chiều về', 'ROUND_TRIP': 'Hai chiều' };

const getPaxInfo = (bk) => {
    const adults = bk.adults_count || 0;
    const children = bk.children_count || 0;
    const total = adults + children;
    if (children === 0) return `${adults} Khách NL`;
    return `${total} Khách (${adults} NL, ${children} TE)`;
};

const getRoomInfo = (bk) => {
    if (!bk.rooms || bk.rooms.length === 0) return '';
    const details = bk.rooms.map(r => `   - ${r.room_type} (x${r.quantity})${r.note ? ': ' + r.note : ''}`).join('\n');
    return `Phòng/Cabin:\n${details}`;
};

const getExtraInfo = (bk) => {
    if (!bk.extras || bk.extras.length === 0) return '';
    const details = bk.extras.map(e => `   - ${e.title} (x${e.quantity})${e.note ? ': ' + e.note : ''}`).join('\n');
    return `Dịch vụ khác:\n${details}`;
};

const getTransportDetails = (bk) => {
    if (!bk.transports || bk.transports.length === 0) return '';
    const includedTransports = bk.transports.filter(t => t.payment_method === 'INCLUDED');
    if (includedTransports.length === 0) return 'Khách tự túc di chuyển';

    return includedTransports.map((t, index) => {
        let dirLabel = t.direction === 'ONE_WAY_TO' ? 'Chiều đi' : (t.direction === 'ONE_WAY_FROM' ? 'Chiều về' : 'Hai chiều');
        let timeStr = t.pickup_datetime && t.pickup_datetime.includes('T') ? ` (${t.pickup_datetime.split('T')[1].substring(0, 5)})` : '';
        const supplierName = t.supplier && t.supplier.name ? t.supplier.name : '';
        const driverInfo = t.driver_info ? ` - LX: ${t.driver_info}` : '';
        const noteInfo = t.note ? ` - Note: ${t.note}` : '';
        return `   ${index + 1}. ${dirLabel}: ${t.vehicle_type || 'Xe'} ${supplierName ? `(${supplierName})` : ''}\n      Đón: ${t.pickup_point || '???'}${timeStr} - Trả: ${t.dropoff_point || '???'}${driverInfo}${noteInfo}`;
    }).join('\n');
};

const getTransportInfo = (bk, tours) => {
    const tourInfo = tours.find(t => t.tour_code === bk.tour);
    const defaultCarType = tourInfo ? (tourInfo.transport_option || 'Theo tour') : 'Theo tour';
    const includedTransports = (bk.transports || []).filter(t => t.payment_method === 'INCLUDED');

    if (includedTransports.length === 0) return { carTypeText: defaultCarType, pickupText: 'Khách tự túc', carTypeHtml: defaultCarType, pickupHtml: 'Khách tự túc' };

    const carTypes = [], pickups = [];
    includedTransports.forEach(t => {
        let dirLabel = t.direction === 'ONE_WAY_TO' ? '[Đi]' : (t.direction === 'ONE_WAY_FROM' ? '[Về]' : '[2 Chiều]');
        carTypes.push(`${dirLabel} ${t.vehicle_type || 'Xe'}`);
        let timeStr = t.pickup_datetime && t.pickup_datetime.includes('T') ? ` (${t.pickup_datetime.split('T')[1].substring(0, 5)})` : '';
        pickups.push(`${dirLabel} Đón: ${t.pickup_point || '???'} - Trả: ${t.dropoff_point || '???'}${timeStr}`);
    });

    return { carTypeText: carTypes.join(', '), pickupText: pickups.join(' | '), carTypeHtml: carTypes.join('<br/>'), pickupHtml: pickups.join('<br/>') };
};

const formatGuestDetailsHtml = (guests) => {
    if (!guests || guests.length === 0) return '';
    return guests.map(g => {
        const typeLabel = g.guest_type === 'CHILD' ? 'TE' : (g.guest_type === 'ADULT' ? 'NL' : '');
        const nameWithType = typeLabel ? `${g.name} (${typeLabel})` : g.name;
        return [nameWithType, g.dob ? formatDateDisplay(g.dob) : '', g.nationality, g.passport].filter(Boolean).join(' - ');
    }).join('<br/>');
};

const formatRoomDetailsHtml = (rooms) => {
    if (!rooms || rooms.length === 0) return '';
    return rooms.map(r => `• ${r.room_type} (x${r.quantity})${r.note ? `: ${r.note}` : ''}`).join('<br/>');
};

const BookingDispatchDialog = ({ open, onClose, booking, tours, onViewDetail, showToast }) => {
    const [groups, setGroups] = useState([]);
    const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);

    const [textContent, setTextContent] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [emailSubject, setEmailSubject] = useState('');

    const [viewMode, setViewMode] = useState('EMAIL');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open && booking) {
            // 1. Gom nhóm tất cả dịch vụ theo NCC
            const allItems = [
                ...(booking.tours || []).map(t => ({ ...t, _type: 'TOUR' })),
                ...(booking.transports || []).map(t => ({ ...t, _type: 'TRANSPORT' })),
                ...(booking.rooms || []).map(r => ({ ...r, _type: 'ROOM' })),
                ...(booking.extras || []).map(e => ({ ...e, _type: 'EXTRA' }))
            ];

            const groupsMap = {};
            allItems.forEach(item => {
                const supId = item.supplier || 'unassigned';
                const supName = item.supplier_name || 'Chưa gán NCC';
                
                // SỬA LỖI: Lấy email và cc (nếu có)
                let supEmail = item.supplier_email || '';
                let supCc = item.supplier_cc_emails || '';

                // SỬA LỖI: Fallback logic - Nếu là dịch vụ Tour và chưa có email, 
                // tìm trong danh mục tours chuẩn (giống cách TourOrderDialog hoạt động)
                if (item._type === 'TOUR' && !supEmail) {
                    const matchedTour = tours.find(t => t.tour_code === item.tour || t.tour_code === booking.tour);
                    if (matchedTour) {
                        supEmail = matchedTour.supplier_email || '';
                        supCc = matchedTour.supplier_cc_emails || '';
                    }
                }

                if (!groupsMap[supId]) {
                    groupsMap[supId] = {
                        supplierId: supId,
                        supplierName: supName,
                        email: supEmail,
                        cc: supCc,
                        items: []
                    };
                }
                // Nếu group đã tạo trước đó chưa có email nhưng item hiện tại lại quét ra email, thì cập nhật vào group
                if (!groupsMap[supId].email && supEmail) groupsMap[supId].email = supEmail;
                if (!groupsMap[supId].cc && supCc) groupsMap[supId].cc = supCc;

                groupsMap[supId].items.push(item);
            });

            setGroups(Object.values(groupsMap));
            setSelectedGroupIndex(0);
        } else {
            // Reset dữ liệu khi đóng popup
            setGroups([]);
            setSelectedGroupIndex(0);
        }
    }, [open, booking, tours]); // SỬA LỖI: Thêm 'tours' vào mảng dependency

useEffect(() => {
        if (open && booking && groups.length > 0 && groups[selectedGroupIndex]) {
            const group = groups[selectedGroupIndex];
            
            // Cập nhật thông tin email tự động
            setRecipientEmail(group.email || '');
            setCcEmail(group.cc || '');

            const dominantType = group.items[0]?._type || 'EXTRA';
            const customerName = booking.customer_detail?.name || booking.customer_name || 'Khách lẻ';
            const customerPhone = booking.customer_detail?.phone || '';

            let text = '';
            let html = '';
            let subject = '';

            const tdStyle = `border: 1px solid #333; padding: 5px; vertical-align: top;`;
            const thStyle = `border: 1px solid #333; padding: 5px; background-color: #f2f2f2; text-align: center; font-weight: bold;`;

            // === 1. FORMAT CHO PHÒNG KHÁCH SẠN (ROOM) ===
            if (dominantType === 'ROOM') {
                const checkInDate = group.items[0].check_in ? group.items[0].check_in.split('-').reverse().join('/') : '';
                subject = `[Fola Travel] - Đặt phòng - Ngày ${checkInDate} - 1 BK`;

                text = `[Fola Travel] Đặt phòng - ${group.supplierName} - ${group.items.length} phòng\nFola Travel xin gửi yêu cầu đặt ${group.items.length} phòng như sau:\n==================================\n`;
                let htmlRows = '';

                group.items.forEach((r, i) => {
                    const cIn = r.check_in ? r.check_in.split('-').reverse().join('/') : '---';
                    const cOut = r.check_out ? r.check_out.split('-').reverse().join('/') : '---';
                    text += `${i + 1}. Khách: ${customerName} - SĐT: ${customerPhone}\n   Mã BK: ${booking.booking_code}\n   Hạng phòng: ${r.room_type} (SL: ${r.quantity})\n   Check-in: ${cIn} | Check-out: ${cOut} (${r.nights_count || 1} đêm)\n   Ghi chú: ${r.note || ''}\n----------------------------------\n`;
                    
                    htmlRows += `<tr>
                        <td style="${tdStyle} text-align: center;">${booking.booking_code}</td>
                        <td style="${tdStyle}">${customerName} <br/> ${customerPhone}</td>
                        <td style="${tdStyle}">${r.room_type} <b>(SL: ${r.quantity})</b></td>
                        <td style="${tdStyle} text-align: center; font-weight: bold; color: #ed6c02;">IN: ${cIn} <br/> OUT: ${cOut}</td>
                        <td style="${tdStyle}">${r.note || ''}</td>
                    </tr>`;
                });

                text += `\nĐại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).\nQuý công ty vui lòng xác nhận đặt chỗ tại đây.\nTrân trọng,`;
                
                html = `<html><body style="font-family: Arial; line-height: 1.6;">
                    <div>Fola Travel xin gửi yêu cầu đặt ${group.items.length} phòng:</div>
                    <table style="width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 10px;">
                        <thead><tr><th style="${thStyle}">Mã BK</th><th style="${thStyle}">Khách hàng</th><th style="${thStyle}">Hạng phòng</th><th style="${thStyle}">Check-in / Check-out</th><th style="${thStyle}">Ghi chú</th></tr></thead>
                        <tbody>${htmlRows}</tbody>
                    </table>
                    <div style="margin-top: 15px;"><strong>Đại lý: Fola Travel</strong></div><div>--------------------------------------------------</div><div style="margin-top: 10px;">Mọi thông tin chi tiết vui lòng liên hệ số hotline Fola Travel tại 0363463666 hoặc 0986332366 (Mr. Hùng).</div><div>Quý công ty vui lòng xác nhận đặt chỗ qua email này.</div><div style="margin-top: 10px;">Trân trọng,</div>
                </body></html>`;

            // === 2. FORMAT CHO DỊCH VỤ LẺ (EXTRA) ===
            } else if (dominantType === 'EXTRA') {
                const serviceDate = group.items[0].service_date ? group.items[0].service_date.split('-').reverse().join('/') : '';
                subject = `[Fola Travel] - Đặt dịch vụ - Ngày ${serviceDate} - 1 BK`;

                text = `[Fola Travel] Đặt dịch vụ - ${group.supplierName} - ${group.items.length} booking\nFola Travel xin gửi yêu cầu đặt ${group.items.length} dịch vụ như sau:\n==================================\n`;
                let htmlRows = '';

                group.items.forEach((e, i) => {
                    const sDate = e.service_date ? e.service_date.split('-').reverse().join('/') : '---';
                    text += `${i + 1}. Khách: ${customerName} - SĐT: ${customerPhone}\n   Mã BK: ${booking.booking_code}\n   Dịch vụ: ${e.title} (SL: ${e.quantity})\n   Ngày SD: ${sDate}\n   Ghi chú: ${e.note || ''}\n----------------------------------\n`;
                    htmlRows += `<tr>
                        <td style="${tdStyle} text-align: center;">${booking.booking_code}</td>
                        <td style="${tdStyle}">${customerName} <br/> ${customerPhone}</td>
                        <td style="${tdStyle}">${e.title} <b>(SL: ${e.quantity})</b></td>
                        <td style="${tdStyle} text-align: center; font-weight: bold; color: #ed6c02;">${sDate}</td>
                        <td style="${tdStyle}">${e.note || ''}</td>
                    </tr>`;
                });

                text += `\nĐại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).\nQuý công ty vui lòng xác nhận đặt chỗ tại đây.\nTrân trọng,`;

                html = `<html><body style="font-family: Arial; line-height: 1.6;">
                    <div>Fola Travel xin gửi yêu cầu đặt ${group.items.length} dịch vụ:</div>
                    <table style="width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 10px;">
                        <thead><tr><th style="${thStyle}">Mã BK</th><th style="${thStyle}">Khách hàng</th><th style="${thStyle}">Dịch vụ (SL)</th><th style="${thStyle}">Ngày SD</th><th style="${thStyle}">Ghi chú</th></tr></thead>
                        <tbody>${htmlRows}</tbody>
                    </table>
                    <div style="margin-top: 15px;"><strong>Đại lý: Fola Travel</strong></div><div>--------------------------------------------------</div><div style="margin-top: 10px;">Mọi thông tin chi tiết vui lòng liên hệ số hotline Fola Travel tại 0363463666 hoặc 0986332366 (Mr. Hùng).</div><div>Quý công ty vui lòng xác nhận đặt chỗ qua email này.</div><div style="margin-top: 10px;">Trân trọng,</div>
                </body></html>`;

            // === 3. FORMAT CHO THUÊ XE (TRANSPORT) ===
            } else if (dominantType === 'TRANSPORT') {
                const dateStr = group.items[0].pickup_datetime ? group.items[0].pickup_datetime.split('T')[0].split('-').reverse().join('/') : '';
                subject = `[Fola Travel Booking] - Đặt xe - Ngày ${dateStr} - 1 BK`;

                text = `[Fola Travel Booking] Đặt xe - ${group.supplierName} - ${group.items.length} chuyến\nFola Travel xin gửi yêu cầu đặt ${group.items.length} chuyến xe như sau:\n==================================\n`;
                let htmlRows = '';

                group.items.forEach((t, i) => {
                    const paxParts = [];
                    if (booking.adults_count) paxParts.push(`${booking.adults_count} NL`);
                    if (booking.children_count) paxParts.push(`${booking.children_count} TE`);
                    const paxText = paxParts.length > 0 ? paxParts.join(', ') : '---';

                    const pickupDate = t.pickup_datetime ? t.pickup_datetime.split('T')[0].split('-').reverse().join('/') : '---';

                    text += `${i + 1}. Khách: ${customerName} - SĐT: ${customerPhone}\n   Mã BK: ${booking.booking_code}\n   Số lượng: ${paxText}\n   Ngày đi: ${pickupDate}\n   Loại xe: ${t.vehicle_type} (${DIRECTION_LABELS[t.direction] || t.direction})\n   Điểm đón: ${t.pickup_point || 'Chưa rõ'}\n   Điểm trả: ${t.dropoff_point || 'Chưa rõ'}\n   Ghi chú: ${t.note || ''}\n----------------------------------\n`;
                    
                    htmlRows += `<tr>
                        <td style="${tdStyle} text-align: center;">${booking.booking_code}</td>
                        <td style="${tdStyle}">${customerName} <br/> ${customerPhone}</td>
                        <td style="${tdStyle} text-align: center;">${paxText}</td>
                        <td style="${tdStyle} text-align: center; font-weight: bold; color: #ed6c02;">${pickupDate}</td>
                        <td style="${tdStyle}">${t.vehicle_type}<br/><b>${DIRECTION_LABELS[t.direction] || t.direction}</b></td>
                        <td style="${tdStyle}"><b>Đón:</b> ${t.pickup_point || '---'}<br/><b>Trả:</b> ${t.dropoff_point || '---'}</td>
                        <td style="${tdStyle}">${t.note || ''}</td>
                    </tr>`;
                });

                text += `\nĐại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).\nQuý công ty vui lòng xác nhận đặt chỗ tại đây.\nTrân trọng,`;

                html = `<html><body style="font-family: Arial; line-height: 1.6;">
                    <div style="margin-bottom: 10px;">Fola Travel xin gửi yêu cầu đặt ${group.items.length} chuyến xe như sau:</div>
                    <table style="width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 10px;">
                        <thead><tr><th style="${thStyle}">Mã BK</th><th style="${thStyle}">Khách hàng</th><th style="${thStyle}">Số lượng</th><th style="${thStyle}">Ngày đi</th><th style="${thStyle}">Loại xe & Chiều</th><th style="${thStyle}">Điểm Đón/Trả</th><th style="${thStyle}">Ghi chú</th></tr></thead>
                        <tbody>${htmlRows}</tbody>
                    </table>
                    <div style="margin-top: 15px;"><strong>Đại lý: Fola Travel</strong></div><div>--------------------------------------------------</div><div style="margin-top: 10px;">Mọi thông tin chi tiết vui lòng liên hệ số hotline Fola Travel tại 0363463666 hoặc 0986332366 (Mr. Hùng).</div><div>Quý công ty vui lòng xác nhận đặt chỗ qua email này.</div><div style="margin-top: 10px;">Trân trọng,</div>
                </body></html>`;

            // === 4. FORMAT CHO ĐIỀU TOUR TRỌN GÓI (TOUR) ===
            } else if (dominantType === 'TOUR') {
                const tourItem = group.items[0];
                const dateDisplay = formatDateDisplay(tourItem.start_date);
                const tourInfo = tours.find(t => t.tour_code === tourItem.tour || t.tour_code === booking.tour);
                const catLabel = BOOKING_CATEGORY_LABELS[booking.booking_category] || 'Dịch vụ lẻ';
                const baseTourName = tourInfo ? (tourInfo.base_tour_name || tourInfo.name) : catLabel;

                subject = `[Fola Travel BK] ${baseTourName} - ${dateDisplay} - 1 BK - ${customerName}`;

                // Tạo nội dung Text (Zalo)
                const transportDetails = getTransportDetails(booking);
                let guestDetails = '';
                if (booking.guests && booking.guests.length > 0) {
                    guestDetails = booking.guests.map((g, i) => {
                        const typeLabel = g.guest_type === 'CHILD' ? 'TE' : (g.guest_type === 'ADULT' ? 'NL' : '');
                        const nameWithType = typeLabel ? `${g.name} (${typeLabel})` : g.name;
                        const details = [nameWithType, g.dob ? formatDateDisplay(g.dob) : '', g.nationality, g.passport].filter(Boolean).join(' - ');
                        return `  ${i + 1}. ${details}`;
                    }).join('\n');
                }
                const roomInfo = getRoomInfo(booking);
                const extraInfo = getExtraInfo(booking);

                text = `[Fola Travel BK] Đặt booking ${baseTourName} - ${dateDisplay} - 1 BK\nKính gửi Quý Công ty,\nCông ty du lịch Fola Travel xin gửi thông tin booking như sau:\n-----------------------------------------\nLịch trình / Dịch vụ: ${baseTourName}\nMã booking: ${booking.booking_code}\nTên khách lead: ${customerName}\nSĐT: ${customerPhone}\nThông tin khách: ${getPaxInfo(booking)}\n${guestDetails}`;
                
                if (roomInfo) text += `\n${roomInfo}`;
                text += `\nNgày đi: ${dateDisplay}`;
                if (transportDetails) text += `\nThông tin đón trả:\n${transportDetails}`;
                if (extraInfo) text += `\n${extraInfo}`;
                text += `\nNote: ${booking.special_request || 'Không'}\nĐại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).\nQuý công ty vui lòng xác nhận đặt chỗ tại đây.\nTrân trọng,`;

                // Tạo nội dung HTML (Email)
                const hasAnyRooms = booking.rooms && booking.rooms.length > 0;
                const { carTypeHtml, pickupHtml } = getTransportInfo(booking, tours);
                const guestHtml = formatGuestDetailsHtml(booking.guests);
                const roomHtml = formatRoomDetailsHtml(booking.rooms);

                html = `<html><body style="font-family: Arial, sans-serif; line-height: 1.6;">
                    <div>Kính gửi Quý Công ty,</div>
                    <div style="margin-bottom: 10px;">Công ty du lịch Fola Travel xin gửi thông tin 1 booking như sau:</div>
                    <div style="margin-bottom: 10px;">--------------------------------------------------</div>
                    <div style="margin-bottom: 10px;"><strong>Lịch trình tour:</strong> ${baseTourName}</div>
                    <table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 13px;">
                        <thead>
                            <tr>
                                <th style="${thStyle}">Mã BK</th> <th style="${thStyle}">Tên đại diện</th> <th style="${thStyle}">SĐT</th> <th style="${thStyle}">SL khách</th> <th style="${thStyle} width: 25%;">Thông tin khách</th>
                                ${hasAnyRooms ? `<th style="${thStyle}">Hạng phòng</th>` : ''}
                                <th style="${thStyle}">Ngày đi</th> <th style="${thStyle}">DV xe</th> <th style="${thStyle}">Điểm đón/trả</th> <th style="${thStyle}">Yêu cầu</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style="${tdStyle} font-weight: bold; color: #007373;">${booking.booking_code}</td>
                                <td style="${tdStyle}">${customerName}</td>
                                <td style="${tdStyle}">${customerPhone}</td>
                                <td style="${tdStyle}">${getPaxInfo(booking)}</td>
                                <td style="${tdStyle}">${guestHtml}</td>
                                ${hasAnyRooms ? `<td style="${tdStyle}">${roomHtml}</td>` : ''}
                                <td style="${tdStyle} text-align: center;">${dateDisplay}</td>
                                <td style="${tdStyle}">${carTypeHtml}</td>
                                <td style="${tdStyle}">${pickupHtml}</td>
                                <td style="${tdStyle}">${booking.special_request || 'Không'}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div style="margin-top: 15px;"><strong>Đại lý: Fola Travel</strong></div><div>--------------------------------------------------</div><div style="margin-top: 10px;">Mọi thông tin chi tiết vui lòng liên hệ số hotline Fola Travel tại 0363463666 hoặc 0986332366 (Mr. Hùng).</div><div>Quý công ty vui lòng xác nhận đặt chỗ qua email này.</div><div style="margin-top: 10px;">Trân trọng,</div>
                </body></html>`;
            }

            setEmailSubject(subject);
            setTextContent(text);
            setHtmlContent(html);
        }
    }, [open, booking, groups, selectedGroupIndex, tours]);
    
    const handleSendEmail = async () => {
        if (!recipientEmail) {
            if (showToast) showToast('Vui lòng nhập Email người nhận!', 'error');
            return;
        }
        setSending(true);
        try {
            await API.sendTourOrderEmail({
                email: recipientEmail,
                cc: ccEmail,
                subject: emailSubject,
                content: htmlContent,
                booking_codes: [booking.booking_code]
            });
            if (showToast) showToast(`Đã gửi email thành công tới ${recipientEmail}!`, 'success');
        } catch (error) {
            console.error(error);
            if (showToast) showToast('Gửi mail thất bại. Vui lòng kiểm tra lại.', 'error');
        } finally {
            setSending(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(textContent);
        if (showToast) showToast('Đã copy nội dung (Dạng Text)!', 'success');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ bgcolor: '#007373', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">ĐIỀU HÀNH BOOKING: {booking?.booking_code}</Typography>
                <Button variant="contained" color="warning" size="small" onClick={() => onViewDetail(booking?.booking_code)}>
                    Xem Booking
                </Button>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', gap: 2, height: '500px' }}>
                    <Box sx={{ width: '30%', borderRight: '1px solid #eee', pr: 2, overflowY: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#007373' }}>
                            Lệnh điều theo NCC:
                        </Typography>
                        <List component="nav" sx={{ p: 0 }}>
                            {groups.map((g, idx) => (
                                <ListItem key={g.supplierId} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        selected={selectedGroupIndex === idx}
                                        onClick={() => setSelectedGroupIndex(idx)}
                                        sx={{
                                            borderRadius: 1, border: '1px solid #ddd',
                                            '&.Mui-selected': { bgcolor: '#e0f2f1', borderColor: '#007373' }
                                        }}
                                    >
                                        <ListItemText
                                            primary={g.supplierName}
                                            secondary={`${g.items.length} dịch vụ`}
                                            primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                            {groups.length === 0 && <Typography variant="caption">Không có dịch vụ nào cần điều.</Typography>}
                        </List>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>Tiêu đề:</Typography>
                            <TextField fullWidth size="small" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>
                                    {viewMode === 'EMAIL' ? 'Nội dung Email (HTML):' : 'Nội dung Text (Zalo):'}
                                </Typography>
                                <ToggleButtonGroup value={viewMode} exclusive onChange={(e, val) => val && setViewMode(val)} size="small" sx={{ height: '32px' }}>
                                    <ToggleButton value="EMAIL" sx={{ px: 2, fontSize: '0.75rem', textTransform: 'none' }}><EmailIcon sx={{ mr: 1, fontSize: '1rem' }} /> Email</ToggleButton>
                                    <ToggleButton value="TEXT" sx={{ px: 2, fontSize: '0.75rem', textTransform: 'none' }}><TextSnippetIcon sx={{ mr: 1, fontSize: '1rem' }} /> Text</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <TextField
                                multiline fullWidth
                                value={viewMode === 'EMAIL' ? htmlContent : textContent}
                                onChange={(e) => viewMode === 'EMAIL' ? setHtmlContent(e.target.value) : setTextContent(e.target.value)}
                                sx={{
                                    flex: 1, bgcolor: viewMode === 'EMAIL' ? '#fff3e0' : '#f9fbe7',
                                    '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', fontFamily: 'monospace', fontSize: '0.85rem', overflowY: 'auto' }
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between', bgcolor: '#f9f9f9' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                    {viewMode === 'EMAIL' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField fullWidth size="small" label="Gửi tới (To)" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} sx={{ bgcolor: 'white' }} />
                            <TextField fullWidth size="small" label="CC Email" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} sx={{ bgcolor: 'white' }} />
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} color="inherit" disabled={sending}>Đóng</Button>
                    {viewMode === 'TEXT' && <Button variant="contained" color="success" startIcon={<ContentCopyIcon />} onClick={handleCopy} disabled={sending}>Copy Text</Button>}
                    {viewMode === 'EMAIL' && <Button variant="contained" startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />} onClick={handleSendEmail} disabled={sending || !recipientEmail} sx={{ bgcolor: '#ed6c02', '&:hover': {bgcolor: '#e65100'} }}>{sending ? 'Đang gửi...' : 'Gửi Email'}</Button>}
                </Box>
            </DialogActions>
        </Dialog>
    );
};
export default BookingDispatchDialog;